import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Package, Loader2, AlertCircle, Search, ExternalLink, Printer
} from 'lucide-react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { getSectionPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InventoryDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const perms = getSectionPerms('inventory');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BACKEND_URL}/api/inventory/${productId}`, getAuthHeaders());
        setData(res.data);
      } catch (err) {
        setError('Failed to load inventory details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [productId]);

  const statusColor = (status) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-700';
      case 'sold': return 'bg-red-100 text-red-700';
      case 'assigned_to_dc': return 'bg-amber-100 text-amber-700';
      case 'rented': return 'bg-purple-100 text-purple-700';
      case 'servicing': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const printBarcodes = useCallback(() => {
    if (!data) return;
    const inStockItems = (data.items || []).filter(i => i.status === 'in_stock');
    if (!inStockItems.length) { alert('No in-stock items to print'); return; }
    const companyName = data.company_name || '';

    const printWin = window.open('', '_blank', 'width=800,height=600');
    printWin.document.write(`<!DOCTYPE html><html><head><title>Barcodes - ${data.product_name || ''}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; }
        .grid { display: flex; flex-wrap: wrap; gap: 4px; padding: 8px; }
        .barcode-card {
          border: 1px solid #ccc; padding: 6px 8px; text-align: center;
          width: 220px; page-break-inside: avoid;
        }
        .company { font-size: 9px; color: #555; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
        svg { display: block; margin: 0 auto; }
        .item-text { font-size: 9px; color: #333; margin-top: 1px; font-family: monospace; }
        @media print { body { -webkit-print-color-adjust: exact; } }
      </style></head><body><div class="grid">`);

    inStockItems.forEach(item => {
      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = printWin.document.createElementNS(svgNS, 'svg');
      try {
        JsBarcode(svg, item.item_id, {
          format: 'CODE128',
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 2,
        });
      } catch { return; }
      const card = printWin.document.createElement('div');
      card.className = 'barcode-card';
      card.innerHTML = `<div class="company">${companyName}</div>`;
      card.appendChild(svg);
      card.innerHTML += `<div class="item-text">${item.item_id}</div>`;
      printWin.document.querySelector('.grid').appendChild(card);
    });

    printWin.document.write('</div></body></html>');
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 300);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="inventory-detail-error">
        <button onClick={() => navigate('/admin/inventory')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Inventory
        </button>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
          <AlertCircle className="h-5 w-5" />
          <span>{error || 'Not found'}</span>
        </div>
      </div>
    );
  }

  const inv = data.inventory;
  const items = data.items || [];

  // Collect unique statuses for filter
  const statuses = [...new Set(items.map(i => i.status).filter(Boolean))];

  const filteredItems = items.filter(item => {
    const matchesSearch = !searchQuery ||
      item.item_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sub_rack_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.batch_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count by status
  const statusCounts = {};
  items.forEach(i => {
    const s = i.status || 'unknown';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });

  return (
    <div data-testid="inventory-detail-page">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/inventory')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Inventory
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{data.product_name}</h1>
          {data.product_code && <p className="text-slate-500 font-mono mt-1">{data.product_code}</p>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={printBarcodes} data-testid="print-barcode-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-800 text-white text-xs font-bold uppercase tracking-wider rounded-none hover:bg-slate-900 transition-colors">
            <Printer className="h-3.5 w-3.5" /> Print Barcodes
          </button>
          <div className="bg-green-100 text-green-700 px-4 py-2 rounded-sm text-sm font-bold">
            {inv.quantity} In Stock
          </div>
          <div className="bg-slate-100 text-slate-700 px-4 py-2 rounded-sm text-sm font-bold">
            {data.total_items} Total Items
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {!perms.hidePrices && (
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Purchase Rate</div>
          <div className="text-lg font-bold text-slate-900">₹{inv.purchase_rate?.toLocaleString() || '-'}</div>
        </div>
        )}
        {!perms.hidePrices && (
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Sale Rate</div>
          <div className="text-lg font-bold text-slate-900">₹{inv.sale_rate?.toLocaleString() || '-'}</div>
        </div>
        )}
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Display Rate</div>
          <div className="text-lg font-bold text-orange-600">₹{inv.display_rate?.toLocaleString() || '-'}</div>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">By Status</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.entries(statusCounts).map(([s, c]) => (
              <span key={s} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor(s)}`}>
                {s.replace('_', ' ')}: {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by Item ID, batch, or rack..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="item-search"
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="status-filter"
          className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          {statuses.map(s => (
            <option key={s} value={s}>{s.replace('_', ' ').toUpperCase()}</option>
          ))}
        </select>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Individual Items</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-green-100 text-green-700">In Stock: {statusCounts['in_stock'] || 0}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-red-100 text-red-700">Sold: {statusCounts['sold'] || 0}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-amber-100 text-amber-700">Assigned to DC: {statusCounts['assigned_to_dc'] || 0}</span>
            <span className="text-sm text-slate-500">{filteredItems.length} shown</span>
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="text-center py-12" data-testid="no-items">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No items found</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider">#</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Item ID</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-3">Status</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Display Price</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Rack</div>
              <div className="font-semibold text-slate-700 text-xs uppercase tracking-wider col-span-2">Batch</div>
            </div>

            {filteredItems.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`inv-item-row-${idx}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="text-slate-400 text-sm">{idx + 1}</div>
                <div className="col-span-2">
                  <span className="font-mono font-medium text-slate-900 text-sm">{item.item_id}</span>
                </div>
                <div className="col-span-3 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor(item.status)}`}>
                    {item.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  {item.status === 'sold' && item.sale_invoice_id && (
                    <Link to={`/admin/sale/invoices/${item.sale_invoice_id}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`link-sale-${idx}`}>
                      {item.sale_invoice_number || 'View Invoice'} <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                  {item.status === 'assigned_to_dc' && item.dc_id && (
                    <Link to={`/admin/sale/delivery-challan/${item.dc_id}`}
                      className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium" data-testid={`link-dc-${idx}`}>
                      {item.dc_number || 'View DC'} <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-slate-900">
                    {item.display_rate != null ? `₹${Number(item.display_rate).toLocaleString()}` : '-'}
                  </span>
                </div>
                <div className="col-span-2 text-sm text-slate-600">
                  {item.sub_rack_name || '-'}
                </div>
                <div className="col-span-2 text-xs text-slate-400 font-mono">
                  {item.batch_number || '-'}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
